export type QBankReferenceType = "textbook" | "article";

export interface QBankReferenceDetails {
    page_number?: number | null;
    journal?: string | null;
    first_author?: string | null;
    date_of_publication?: string | null;
    doi?: string | null;
    [key: string]: unknown;
}

export interface QBankReference {
    text_excerpt?: string;
    source_name?: string;
    type?: QBankReferenceType;
    details?: QBankReferenceDetails;
    [key: string]: unknown;
}

export interface QBankReferenceDraft {
    text_excerpt: string;
    source_name: string;
    type: QBankReferenceType;
    page_number: string;
    journal: string;
    first_author: string;
    date_of_publication: string;
    doi: string;
    extraFields: Record<string, unknown>;
    detailsExtraFields: Record<string, unknown>;
}

const TOP_LEVEL_REFERENCE_KEYS = new Set([
    "text_excerpt",
    "excerpt",
    "content",
    "source_name",
    "source",
    "textbook",
    "type",
    "details",
    "page_number",
]);

const DETAIL_REFERENCE_KEYS = new Set([
    "page_number",
    "journal",
    "first_author",
    "date_of_publication",
    "doi",
]);

const isRecord = (value: unknown): value is Record<string, unknown> => (
    Boolean(value) && typeof value === "object" && !Array.isArray(value)
);

const firstString = (...values: unknown[]) => {
    for (const value of values) {
        if (typeof value === "string") return value;
        if (typeof value === "number") return String(value);
    }
    return "";
};

const copyUnknownFields = (
    source: Record<string, unknown>,
    knownKeys: Set<string>
): Record<string, unknown> => {
    return Object.entries(source).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (!knownKeys.has(key)) {
            acc[key] = value;
        }
        return acc;
    }, {});
};

export const makeEmptyReferenceDraft = (): QBankReferenceDraft => ({
    text_excerpt: "",
    source_name: "",
    type: "textbook",
    page_number: "",
    journal: "",
    first_author: "",
    date_of_publication: "",
    doi: "",
    extraFields: {},
    detailsExtraFields: {},
});

export const getReferenceExcerpt = (reference: unknown): string => {
    if (!isRecord(reference)) return "";
    return firstString(reference.text_excerpt, reference.excerpt, reference.content).trim();
};

export const getReferenceSource = (reference: unknown): string => {
    if (!isRecord(reference)) return "Source";
    const source = firstString(reference.source_name, reference.source, reference.textbook).trim() || "Source";
    return source.replace(/\.pdf$/i, "");
};

export const getReferencePage = (reference: unknown): string => {
    if (!isRecord(reference)) return "";
    const details = isRecord(reference.details) ? reference.details : {};
    return firstString(details.page_number, reference.page_number).trim();
};

export const normalizeReferenceForEditor = (reference: unknown): QBankReferenceDraft => {
    if (!isRecord(reference)) return makeEmptyReferenceDraft();

    const details = isRecord(reference.details) ? reference.details : {};
    const type = reference.type === "article" ? "article" : "textbook";
    const sourceName = firstString(reference.source_name, reference.source, reference.textbook).trim();

    return {
        text_excerpt: getReferenceExcerpt(reference),
        source_name: sourceName,
        type,
        page_number: getReferencePage(reference),
        journal: firstString(details.journal).trim(),
        first_author: firstString(details.first_author).trim(),
        date_of_publication: firstString(details.date_of_publication).trim(),
        doi: firstString(details.doi).trim(),
        extraFields: copyUnknownFields(reference, TOP_LEVEL_REFERENCE_KEYS),
        detailsExtraFields: copyUnknownFields(details, DETAIL_REFERENCE_KEYS),
    };
};

export const normalizeReferencesForEditor = (references: unknown): QBankReferenceDraft[] => {
    if (!Array.isArray(references)) return [];
    return references.map(normalizeReferenceForEditor);
};

const parsePageNumber = (value: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return parsed;
};

const hasUnknownFields = (fields: Record<string, unknown>) => Object.keys(fields).length > 0;

export const referenceDraftHasContent = (reference: QBankReferenceDraft): boolean => {
    return Boolean(
        reference.text_excerpt.trim() ||
        reference.source_name.trim() ||
        reference.page_number.trim() ||
        reference.journal.trim() ||
        reference.first_author.trim() ||
        reference.date_of_publication.trim() ||
        reference.doi.trim() ||
        hasUnknownFields(reference.extraFields) ||
        hasUnknownFields(reference.detailsExtraFields)
    );
};

export const serializeReferencesForSave = (
    references: QBankReferenceDraft[]
): QBankReference[] => {
    return references
        .filter(referenceDraftHasContent)
        .map((reference) => ({
            ...reference.extraFields,
            text_excerpt: reference.text_excerpt.trim(),
            source_name: reference.source_name.trim(),
            type: reference.type,
            details: {
                ...reference.detailsExtraFields,
                page_number: parsePageNumber(reference.page_number),
                journal: reference.journal.trim() || null,
                first_author: reference.first_author.trim() || null,
                date_of_publication: reference.date_of_publication.trim() || null,
                doi: reference.doi.trim() || null,
            },
        }));
};
